import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import apiService from '@/api-service';
import { BestekFilterParams, mapFilterParamsToQueryParams, initialBestekFilterParams } from '../bestek-filter/bestek-filter-params';

interface ExportColumn {
    key: string;
    label: string;
    category: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
    filterParams: BestekFilterParams;
    totalCount: number;
}

const CSV_EXPORT_COLUMNS: ExportColumn[] = [
    // SAFE: Alleen basis Bestek velden die direct op het model staan
    { key: 'registratie_nummer', label: 'Registratienummer', category: 'Bestek' },
    { key: 'nummer', label: 'Nummer', category: 'Bestek' },
    { key: 'versie_jaarversie', label: 'Jaarversie', category: 'Bestek' },
    { key: 'wijzigings_datum', label: 'Wijzigingsdatum', category: 'Bestek' },
    { key: 'aanmaak_datum', label: 'Aanmaakdatum', category: 'Bestek' },
    { key: 'registratie_datum', label: 'Registratiedatum', category: 'Bestek' },
    { key: 'ontgrendel_code', label: 'Ontgrendelcode', category: 'Bestek' },
    { key: 'korte_beschrijving', label: 'Korte Beschrijving', category: 'Bestek' },
    
    // SAFE: Overeenkomsthouder velden (deze werken in de serializer)
    { key: 'overeenkomst_houder_nummer', label: 'OVK Nummer', category: 'Overeenkomsthouder' },
    { key: 'overeenkomst_houder_naam', label: 'OVK Naam', category: 'Overeenkomsthouder' },
    
    // REMOVED: Alle locatie velden (veroorzaken locatie_id errors)
    // { key: 'locatie_provincie', label: 'Provincie', category: 'Locatie' },
    // { key: 'locatie_gemeente', label: 'Gemeente', category: 'Locatie' },
    // { key: 'locatie_stad', label: 'Stad/Dorp', category: 'Locatie' },
    // { key: 'locatie', label: 'Locatie Details', category: 'Locatie' },
    
    // SAFE: Algemene info velden (deze werken in de serializer)
    { key: 'beschrijving', label: 'Beschrijving', category: 'Algemene Info' },
    { key: 'opdrachtgever', label: 'Opdrachtgever', category: 'Algemene Info' },
    { key: 'tijdsbepaling', label: 'Tijdsbepaling', category: 'Algemene Info' },
    { key: 'directie', label: 'Directie', category: 'Algemene Info' },
    { key: 'onderhoudstermijn', label: 'Onderhoudstermijn', category: 'Algemene Info' },
];

export default function CSVExportModal({ show, onHide, filterParams, totalCount }: Props) {
    const [selectedColumns, setSelectedColumns] = useState<ExportColumn[]>([
        CSV_EXPORT_COLUMNS.find(c => c.key === 'registratie_nummer')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'korte_beschrijving')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'overeenkomst_houder_naam')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'versie_jaarversie')!,
    ]);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx');
    const [maxRows, setMaxRows] = useState<number>(Math.min(1000, totalCount));

    // Groepeer kolommen per categorie voor betere UX
    const groupedColumns = CSV_EXPORT_COLUMNS.reduce((groups, column) => {
        if (!groups[column.category]) {
            groups[column.category] = [];
        }
        groups[column.category].push(column);
        return groups;
    }, {} as { [category: string]: ExportColumn[] });

    const handleExport = async () => {
        if (selectedColumns.length === 0) {
            alert('Selecteer minimaal één kolom om te exporteren.');
            return;
        }

        setIsExporting(true);
        
        try {
            // Debug: log the original filterParams
            console.log('Original filterParams:', filterParams);
            
            // Create the search_params based on what the backend expects
            const mappedParams = mapFilterParamsToQueryParams(filterParams);
            console.log('Mapped filterParams:', mappedParams);
            
            // Create the export request that matches the backend RequestData class
            // IMPORTANT: Don't add extra fields that the backend doesn't expect
            const exportParams = {
                search_params: mappedParams,
                format: exportFormat,
                export_columns: selectedColumns.map(col => col.key),
                max_rows: maxRows
                // REMOVED: include_usage, pagesize, page - these cause backend field mapping errors
            };

            console.log('Final export request:', exportParams);

            const response = await apiService.post('/bestek/export', exportParams, {
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], {
                type: exportFormat === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `bestekken_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            onHide();
        } catch (error: any) {
            console.error('Export failed:', error);
            
            // Try to get the actual error message from the response
            if (error.response?.data) {
                try {
                    if (error.response.data instanceof Blob) {
                        const errorText = await error.response.data.text();
                        console.error('Backend error details:', errorText);
                        alert(`Export mislukt: ${errorText}`);
                    } else {
                        console.error('Backend error:', error.response.data);
                        alert(`Export mislukt: ${JSON.stringify(error.response.data)}`);
                    }
                } catch (parseError) {
                    console.error('Could not parse error response');
                    alert('Export mislukt. Check console voor details.');
                }
            } else {
                alert('Export mislukt. Probeer het opnieuw.');
            }
        } finally {
            setIsExporting(false);
        }
    };

    const selectAllInCategory = (category: string) => {
        const categoryColumns = groupedColumns[category];
        const newSelected = [...selectedColumns];
        
        categoryColumns.forEach(col => {
            if (!newSelected.find(selected => selected.key === col.key)) {
                newSelected.push(col);
            }
        });
        
        setSelectedColumns(newSelected);
    };

    const deselectAllInCategory = (category: string) => {
        const categoryColumns = groupedColumns[category];
        const newSelected = selectedColumns.filter(col => 
            !categoryColumns.find(catCol => catCol.key === col.key)
        );
        setSelectedColumns(newSelected);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Bestekken Exporteren naar {exportFormat.toUpperCase()}</Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                <div className="mb-3">
                    <strong>Export instellingen:</strong>
                    <div className="mt-2">
                        <Form.Check
                            type="radio"
                            id="format-csv"
                            label="CSV formaat"
                            checked={exportFormat === 'csv'}
                            onChange={() => setExportFormat('csv')}
                        />
                        <Form.Check
                            type="radio"
                            id="format-excel"
                            label="Excel formaat"
                            checked={exportFormat === 'xlsx'}
                            onChange={() => setExportFormat('xlsx')}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <Form.Label>Maximum aantal rijen (max {totalCount}):</Form.Label>
                    <Form.Control
                        type="number"
                        value={maxRows}
                        onChange={(e) => setMaxRows(Math.min(parseInt(e.target.value) || 1000, totalCount))}
                        min={1}
                        max={totalCount}
                    />
                </div>

                <div className="mb-3">
                    <Form.Label>Geselecteerde kolommen ({selectedColumns.length}):</Form.Label>
                    <Select
                        isMulti
                        value={selectedColumns}
                        onChange={(newValue) => setSelectedColumns([...newValue])}
                        options={CSV_EXPORT_COLUMNS}
                        getOptionLabel={(option) => `${option.label} (${option.category})`}
                        getOptionValue={(option) => option.key}
                        placeholder="Selecteer kolommen..."
                        className="mb-2"
                    />
                </div>

                <div>
                    <strong>Kolommen per categorie:</strong>
                    {Object.entries(groupedColumns).map(([category, columns]) => (
                        <div key={category} className="mt-2 p-2 border rounded">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>{category}</strong>
                                <div>
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        className="me-1"
                                        onClick={() => selectAllInCategory(category)}
                                    >
                                        Alles selecteren
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => deselectAllInCategory(category)}
                                    >
                                        Alles deselecteren
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="row">
                                {columns.map(column => (
                                    <div key={column.key} className="col-md-6">
                                        <Form.Check
                                            type="checkbox"
                                            id={`col-${column.key}`}
                                            label={column.label}
                                            checked={selectedColumns.some(sel => sel.key === column.key)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedColumns([...selectedColumns, column]);
                                                } else {
                                                    setSelectedColumns(selectedColumns.filter(sel => sel.key !== column.key));
                                                }
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Modal.Body>
            
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isExporting}>
                    Annuleren
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleExport}
                    disabled={isExporting || selectedColumns.length === 0}
                >
                    {isExporting ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Exporteren...
                        </>
                    ) : (
                        `Exporteer ${selectedColumns.length} kolommen`
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}